import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRyanAuth } from '../context/RyanAuthContext';
import {
  User, BookOpen, Shield, Package, Camera,
  ShieldCheck, Star,
} from 'lucide-react';
import './RyanStudentAreaPage.css';

/* ── Ilustrações decorativas do fundo — cópia independente das usadas na
   RyanLoginPage (mesmo conceito visual, mas sem componente compartilhado,
   a pedido do usuário), traço branco translúcido sobre o gradiente verde
   da página, motivos ligados à medicina. ── */
const RsaIconStethoscope: React.FC = () => (
  <svg viewBox="0 0 100 120" fill="none">
    <g stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 6 V26 C22 40 32 48 44 48 C56 48 66 40 66 26 V14" />
      <circle cx="22" cy="6" r="4" />
      <circle cx="66" cy="14" r="4" />
      <path d="M44 48 V70 C44 82 54 90 54 90" />
      <circle cx="60" cy="96" r="12" />
      <circle cx="60" cy="96" r="4.5" />
    </g>
  </svg>
);

const RsaIconAsclepius: React.FC = () => (
  <svg viewBox="0 0 100 130" fill="none">
    <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <line x1="50" y1="10" x2="50" y2="122" strokeWidth={3.2} />
      <path d="M50 24 C68 32 69 48 50 56 C31 64 30 80 50 88 C68 95 69 110 50 118" strokeWidth={5.5} fill="none" />
      <path d="M58 30 L64 26 M61 44 L68 42 M58 52 L64 56 M42 64 L36 60 M39 78 L32 79 M42 86 L36 90 M58 94 L65 91 M61 108 L68 111" strokeWidth={2} opacity="0.85" />
      <path d="M50 6 L60 20 L55 31 L50 34 L45 31 L40 20 Z" fill="currentColor" opacity="0.7" stroke="none" />
      <circle cx="45.5" cy="19" r="1.8" fill="currentColor" stroke="none" />
      <circle cx="54.5" cy="19" r="1.8" fill="currentColor" stroke="none" />
      <path d="M50 6 l-3 -6 M50 6 l3 -6 M50 6 l0 -4" strokeWidth={1.6} opacity="0.7" />
    </g>
  </svg>
);

const RsaIconHospital: React.FC = () => (
  <svg viewBox="0 0 100 100" fill="none">
    <g stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
      <rect x="12" y="34" width="76" height="58" rx="2" />
      <path d="M50 44 v20 M40 54 h20" strokeWidth={3.2} />
      <line x1="22" y1="66" x2="30" y2="66" />
      <line x1="70" y1="66" x2="78" y2="66" />
      <line x1="22" y1="78" x2="30" y2="78" />
      <line x1="70" y1="78" x2="78" y2="78" />
      <rect x="44" y="76" width="12" height="16" />
    </g>
  </svg>
);

const RsaIconHeartSus: React.FC = () => (
  <svg viewBox="0 0 140 110" fill="none">
    <g stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
      <path d="M70 34 C 70 18 90 14 96 28 C 102 40 90 52 70 66 C 50 52 38 40 44 28 C 50 14 70 18 70 34 Z" />
      <path d="M70 30 v18 M61 39 h18" strokeWidth={3} />
    </g>
    <text x="70" y="94" textAnchor="middle" fontSize="13" fontWeight={700} fill="currentColor" fontFamily="Inter, sans-serif" letterSpacing="0.5">
      #VivaoSUS
    </text>
  </svg>
);

const RsaIconSyringe: React.FC = () => (
  <svg viewBox="0 0 150 40" fill="none">
    <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="11" x2="8" y2="29" strokeWidth={3.2} />
      <line x1="8" y1="20" x2="28" y2="20" strokeWidth={2.8} />
      <line x1="28" y1="13" x2="28" y2="27" strokeWidth={2.4} />
      <rect x="30" y="11" width="52" height="18" rx="2" strokeWidth={2.8} />
      <line x1="42" y1="14" x2="42" y2="26" strokeWidth={1.6} />
      <line x1="54" y1="14" x2="54" y2="26" strokeWidth={1.6} />
      <line x1="66" y1="14" x2="66" y2="26" strokeWidth={1.6} />
      <line x1="78" y1="14" x2="78" y2="26" strokeWidth={1.6} />
      <path d="M82 12 L96 17 L96 23 L82 28" strokeWidth={2.4} />
      <line x1="96" y1="20" x2="140" y2="20" strokeWidth={2.2} />
      <circle cx="144" cy="20" r="2" fill="currentColor" stroke="none" />
    </g>
  </svg>
);

const RsaIconPillPack: React.FC = () => (
  <svg viewBox="0 0 100 60" fill="none">
    <g stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="6" width="88" height="48" rx="6" />
      <circle cx="24" cy="20" r="7" />
      <circle cx="50" cy="20" r="7" />
      <circle cx="76" cy="20" r="7" />
      <circle cx="24" cy="40" r="7" />
      <circle cx="50" cy="40" r="7" />
      <circle cx="76" cy="40" r="7" />
    </g>
  </svg>
);

const RsaIconAmbulance: React.FC = () => (
  <svg viewBox="0 0 140 80" fill="none">
    <g stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 54 V30 h64 v-8 h28 l16 18 v14" />
      <rect x="6" y="30" width="64" height="24" />
      <path d="M70 30 h28 l16 18 h-44 Z" />
      <circle cx="30" cy="60" r="8" />
      <circle cx="98" cy="60" r="8" />
      <line x1="38" y1="60" x2="90" y2="60" />
      <path d="M28 36 v10 M23 41 h10" strokeWidth={2.4} />
    </g>
  </svg>
);

const RsaIconCross: React.FC = () => (
  <svg viewBox="0 0 40 40" fill="none">
    <path d="M20 6 v28 M6 20 h28" stroke="currentColor" strokeWidth={5} strokeLinecap="round" />
  </svg>
);

const RsaIconPulseLine: React.FC = () => (
  <svg viewBox="0 0 140 40" fill="none">
    <path
      d="M0 20 H18 Q22 12 26 20 H34 L38 27 L41 3 L45 31 L49 20 H58 Q64 12 70 20 H140"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RyanStudentBackground: React.FC = () => (
  <div className="rsa-bg-icons" aria-hidden="true">
    <div className="rsa-bg-icon rsa-bg-icon--stethoscope"><RsaIconStethoscope /></div>
    <div className="rsa-bg-icon rsa-bg-icon--asclepius"><RsaIconAsclepius /></div>
    <div className="rsa-bg-icon rsa-bg-icon--hospital"><RsaIconHospital /></div>
    <div className="rsa-bg-icon rsa-bg-icon--heart-sus"><RsaIconHeartSus /></div>
    <div className="rsa-bg-icon rsa-bg-icon--syringe"><RsaIconSyringe /></div>
    <div className="rsa-bg-icon rsa-bg-icon--pillpack"><RsaIconPillPack /></div>
    <div className="rsa-bg-icon rsa-bg-icon--ambulance"><RsaIconAmbulance /></div>
    <div className="rsa-bg-icon rsa-bg-icon--cross-a"><RsaIconCross /></div>
    <div className="rsa-bg-icon rsa-bg-icon--cross-b"><RsaIconCross /></div>
    <div className="rsa-bg-icon rsa-bg-icon--pulse"><RsaIconPulseLine /></div>
    <div className="rsa-bg-icon rsa-bg-icon--stethoscope-sm"><RsaIconStethoscope /></div>
  </div>
);

type Tab = 'profile' | 'accesses' | 'security';

interface ProfileData {
  phone: string | null;
  birthDate: string | null;
  periodo: number | null;
}

interface RyanSaleItem {
  _id: string;
  productId: {
    _id: string;
    title: string;
    periodo: number | null;
    spaceIds: { _id: string; name: string }[];
  } | null;
  priceCents: number;
  grantedAt: string;
  expiresAt: string | null;
  durationDays: number | null;
  status: 'active' | 'expiring' | 'expired';
  daysLeft: number | null;
}

interface PwForm {
  current: string;
  next: string;
  confirm: string;
}

const PERIODOS = Array.from({ length: 12 }, (_, i) => i + 1);

const RyanStudentAreaPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, reloadProfile, isAdmin } = useAuth();
  const { isVip } = useRyanAuth();

  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [tabKey, setTabKey] = useState(0);
  const [loading, setLoading] = useState(true);

  /* Profile */
  const [profileData, setProfileData] = useState<ProfileData>({ phone: null, birthDate: null, periodo: null });
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [userAuthProvider, setUserAuthProvider] = useState('local');
  const [editUsername, setEditUsername] = useState('');
  const [usernameError, setUsernameError] = useState<string | null>(null);

  /* Photo */
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Accesses */
  const [sales, setSales] = useState<RyanSaleItem[]>([]);
  const [loadingSales, setLoadingSales] = useState(false);

  /* Password */
  const [changingPassword, setChangingPassword] = useState(false);
  const [pwForm, setPwForm] = useState<PwForm>({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  const loadProfile = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch('/api/users/profile', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setUserAuthProvider(data.authProvider || 'local');
        const periodoNum = data.periodo ?? (data.semester ? parseInt(data.semester) || null : null);

        // User.phone (campo genérico, do lado ENEM) nunca é escrito no
        // cadastro/checkout do RYAN — quem captura o WhatsApp ali é
        // RyanAccess.whatsapp. Causa raiz do campo aparecer vazio na Área do
        // Aluno mesmo pra quem já informou o WhatsApp no checkout: os dois
        // campos nunca foram sincronizados. Fallback aqui (só quando
        // User.phone ainda está vazio) resolve isso sem impedir edição — ao
        // salvar, continua indo pra User.phone normalmente, que passa a ser
        // a fonte pra próxima vez.
        let phone = data.phone || null;
        if (!phone) {
          try {
            const ryanRes = await fetch('/api/ryan/me', { headers: { Authorization: `Bearer ${token}` } });
            if (ryanRes.ok) {
              const ryanData = await ryanRes.json();
              if (ryanData.whatsapp) phone = ryanData.whatsapp;
            }
          } catch {}
        }

        setProfileData({
          phone,
          birthDate: data.birthDate ? data.birthDate.split('T')[0] : null,
          periodo: periodoNum,
        });
      }
    } catch {}
  }, []);

  const loadSales = useCallback(async () => {
    setLoadingSales(true);
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch('/api/ryan/sales/my-sales', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSales(data.sales || []);
      }
    } catch {} finally { setLoadingSales(false); }
  }, []);

  useEffect(() => {
    loadProfile().finally(() => setLoading(false));
  }, [loadProfile]);

  useEffect(() => {
    if (activeTab === 'accesses') loadSales();
  }, [activeTab, loadSales]);

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    setTabKey(k => k + 1);
    setChangingPassword(false);
    setPwForm({ current: '', next: '', confirm: '' });
    setPwError(null);
    setPwSuccess(false);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setUsernameError(null);
    const token = localStorage.getItem('accessToken');
    const trimmedUsername = editUsername.trim();
    const usernameChanged = trimmedUsername !== (user?.username || '');

    try {
      const [academicRes, usernameResult] = await Promise.all([
        fetch('/api/users/profile/academic', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            phone: profileData.phone,
            birthDate: profileData.birthDate,
            semester: profileData.periodo ? String(profileData.periodo) : '',
            periodo: profileData.periodo,
          }),
        }),
        usernameChanged
          ? fetch('/api/ryan/auth/username', {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ username: trimmedUsername }),
            })
          : Promise.resolve(null),
      ]);

      let usernameOk = true;
      if (usernameResult) {
        if (usernameResult.ok) {
          usernameOk = true;
        } else {
          usernameOk = false;
          const d = await usernameResult.json().catch(() => ({}));
          setUsernameError(d.message || 'Erro ao alterar nome de usuário.');
        }
      }

      if (academicRes.ok) {
        await reloadProfile();
      }

      // Só fecha o formulário se tudo deu certo — se o username colidiu, o
      // aluno continua vendo o form aberto com o erro, mas os outros campos
      // (período/celular/nascimento) já foram salvos normalmente.
      if (academicRes.ok && usernameOk) {
        setEditingProfile(false);
      }
    } catch {} finally { setSavingProfile(false); }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      alert('Selecione uma imagem PNG, JPG ou WEBP'); return;
    }
    if (file.size > 5 * 1024 * 1024) { alert('Máximo 5MB'); return; }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleUploadPhoto = async () => {
    if (!photoFile) return;
    setUploadingPhoto(true);
    const token = localStorage.getItem('accessToken');
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);
      const res = await fetch('/api/users/profile/photo', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        setPhotoFile(null);
        setPhotoPreview(null);
        await loadProfile();
        await reloadProfile();
      } else {
        const d = await res.json();
        alert(d.message || 'Erro ao fazer upload');
      }
    } catch { alert('Erro ao fazer upload da foto'); } finally { setUploadingPhoto(false); }
  };

  const handleCancelPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleChangePassword = async () => {
    setPwError(null);
    if (pwForm.next.length < 6) { setPwError('A nova senha deve ter pelo menos 6 caracteres.'); return; }
    if (pwForm.next !== pwForm.confirm) { setPwError('As senhas não coincidem.'); return; }
    setPwSaving(true);
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch('/api/ryan/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.next }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwSuccess(true);
        setTimeout(() => {
          setChangingPassword(false);
          setPwForm({ current: '', next: '', confirm: '' });
          setPwSuccess(false);
        }, 2200);
      } else {
        setPwError(data.message || 'Erro ao alterar senha.');
      }
    } catch {
      setPwError('Erro de conexão. Tente novamente.');
    } finally {
      setPwSaving(false);
    }
  };

  const fmtDate = (d: string | null) => {
    if (!d) return '—';
    const dt = new Date(d);
    const offset = dt.getTimezoneOffset() * 60000;
    return new Date(dt.getTime() + offset).toLocaleDateString('pt-BR');
  };

  const getSaleStatus = (sale: RyanSaleItem) => {
    if (!sale.expiresAt) return { label: 'Vitalício', variant: 'lifetime', pct: 100 };
    const days = sale.daysLeft ?? 0;
    if (days < 0) return { label: 'Expirado', variant: 'expired', pct: 0 };
    if (days <= 14) return { label: `${days} dias restantes`, variant: 'expiring', pct: Math.max(2, (days / 14) * 100) };
    const total = sale.durationDays || 90;
    const pct = Math.min(100, Math.round((days / total) * 100));
    return { label: `${days} dias restantes`, variant: 'active', pct };
  };

  if (loading) {
    return (
      <div className="rsa-page">
        <div className="rsa-fullscreen-loader">
          <div className="rsa-spinner" />
        </div>
      </div>
    );
  }

  const username = user?.username || '';
  const profilePhoto = user?.profilePhoto || null;
  const displayPhoto = photoPreview || profilePhoto;
  const userInitial = (username || '?')[0].toUpperCase();
  const isGoogleAuth = userAuthProvider === 'google' || userAuthProvider === 'local+google';

  return (
    <div className="rsa-page">
      <RyanStudentBackground />

      {/* ── Header — sem demarcação, fundido com o fundo da página. Logo à
          esquerda do título funciona como botão de voltar pro RyanHomePage. ── */}
      <header className="rsa-header">
        <div className="rsa-header-brand">
          <button
            className="rsa-header-logo-btn"
            onClick={() => navigate('/ryan')}
            title="Voltar para o início"
          >
            <img src="/Ryan/graia-logo-branca.png" alt="Graia Medicina" className="rsa-header-logo" />
          </button>
          <h1 className="rsa-header-title">
            <span>Área do</span>
            <span>Aluno</span>
          </h1>
        </div>
      </header>

      {/* ── Hero card ── */}
      <div className="rsa-hero-wrap">
        <div className="rsa-hero-card">

          {/* Foto */}
          <div className="rsa-photo-col">
            <div className="rsa-photo-circle-wrap">
              {/* inner circular container — overflow:hidden aqui, não no wrap (câmera fica fora) */}
              <div className="rsa-photo-circle-inner">
                {displayPhoto ? (
                  <img src={displayPhoto} alt="Foto de perfil" className="rsa-photo-circle" />
                ) : (
                  <div className="rsa-photo-initial">{userInitial}</div>
                )}
              </div>
              <button
                className="rsa-photo-camera-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Alterar foto"
              >
                <Camera size={14} />
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />

            {photoFile ? (
              <div className="rsa-photo-actions">
                <button
                  className="rsa-btn-save-photo"
                  onClick={handleUploadPhoto}
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? 'Enviando...' : 'Salvar foto'}
                </button>
                <button className="rsa-btn-cancel-photo" onClick={handleCancelPhoto}>
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                className="rsa-btn-alter-photo"
                onClick={() => fileInputRef.current?.click()}
              >
                Alterar foto
              </button>
            )}
          </div>

          {/* Info */}
          <div className="rsa-hero-info">
            <div className="rsa-hero-name-row">
              <h2 className="rsa-hero-name">{username}</h2>
              {isAdmin ? (
                <span className="rsa-admin-badge">
                  <ShieldCheck size={11} /> Admin
                </span>
              ) : isVip ? (
                <span className="rsa-vip-badge">
                  <Star size={10} /> VIP
                </span>
              ) : null}
            </div>
            <p className="rsa-hero-email">{user?.email}</p>
            {profileData.periodo && (
              <p className="rsa-hero-periodo">{profileData.periodo}° Período</p>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="rsa-tabs-row">
        <button
          className={`rsa-tab${activeTab === 'profile' ? ' rsa-tab--active' : ''}`}
          onClick={() => switchTab('profile')}
        >
          <User size={15} /> Perfil
        </button>
        <button
          className={`rsa-tab${activeTab === 'accesses' ? ' rsa-tab--active' : ''}`}
          onClick={() => switchTab('accesses')}
        >
          <Package size={15} /> Meus Acessos
        </button>
        <button
          className={`rsa-tab${activeTab === 'security' ? ' rsa-tab--active' : ''}`}
          onClick={() => switchTab('security')}
        >
          <Shield size={15} /> Segurança
        </button>
      </div>

      {/* ── Content ── */}
      <div key={tabKey} className="rsa-content rsa-content--animated">

        {/* ── ABA PERFIL ── */}
        {activeTab === 'profile' && (
          <div className="rsa-green-card">
            <div className="rsa-card-title-row">
              <h3 className="rsa-card-title">Dados Pessoais</h3>
              {!editingProfile && (
                <button
                  className="rsa-btn-edit"
                  onClick={() => {
                    setEditUsername(user?.username || '');
                    setUsernameError(null);
                    setEditingProfile(true);
                  }}
                >
                  Editar
                </button>
              )}
            </div>

            {editingProfile ? (
              <div className="rsa-form">
                <div className="rsa-form-group">
                  <label className="rsa-form-label">Nome de usuário</label>
                  <input
                    type="text"
                    className="rsa-form-input"
                    value={editUsername}
                    onChange={e => { setEditUsername(e.target.value); setUsernameError(null); }}
                  />
                  {usernameError && <div className="rsa-pw-error">{usernameError}</div>}
                </div>
                <div className="rsa-form-group">
                  <label className="rsa-form-label">Período</label>
                  <select
                    className="rsa-form-input"
                    value={profileData.periodo ?? ''}
                    onChange={e => setProfileData(p => ({
                      ...p,
                      periodo: e.target.value ? parseInt(e.target.value) : null,
                    }))}
                  >
                    <option value="">Selecione o período</option>
                    {PERIODOS.map(p => (
                      <option key={p} value={p}>{p}° Período</option>
                    ))}
                  </select>
                </div>
                <div className="rsa-form-group">
                  <label className="rsa-form-label">Celular / WhatsApp</label>
                  <input
                    type="tel"
                    className="rsa-form-input"
                    placeholder="(00) 00000-0000"
                    value={profileData.phone || ''}
                    onChange={e => setProfileData(p => ({ ...p, phone: e.target.value }))}
                  />
                </div>
                <div className="rsa-form-group">
                  <label className="rsa-form-label">Data de Nascimento</label>
                  <input
                    type="date"
                    className="rsa-form-input"
                    value={profileData.birthDate || ''}
                    onChange={e => setProfileData(p => ({ ...p, birthDate: e.target.value }))}
                  />
                </div>
                <div className="rsa-form-actions">
                  <button
                    className="rsa-btn-cancel"
                    onClick={() => { setEditingProfile(false); setUsernameError(null); }}
                  >
                    Cancelar
                  </button>
                  <button className="rsa-btn-save" onClick={handleSaveProfile} disabled={savingProfile}>
                    {savingProfile ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="rsa-info-grid">
                <div className="rsa-info-item">
                  <span className="rsa-info-label">Nome</span>
                  <span className="rsa-info-value">{username || '—'}</span>
                </div>
                <div className="rsa-info-item">
                  <span className="rsa-info-label">Período</span>
                  <span className="rsa-info-value">
                    {profileData.periodo ? `${profileData.periodo}° Período` : 'Não informado'}
                  </span>
                </div>
                <div className="rsa-info-item">
                  <span className="rsa-info-label">Celular / WhatsApp</span>
                  <span className="rsa-info-value">{profileData.phone || 'Não informado'}</span>
                </div>
                <div className="rsa-info-item">
                  <span className="rsa-info-label">Data de Nascimento</span>
                  <span className="rsa-info-value">{fmtDate(profileData.birthDate)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── ABA ACESSOS ── */}
        {activeTab === 'accesses' && (
          <div className="rsa-green-card">
            <div className="rsa-card-title-row">
              <h3 className="rsa-card-title">Meus Acessos</h3>
            </div>

            {isAdmin && (
              <div className="rsa-special-access rsa-special-access--admin">
                <div className="rsa-special-access-icon">
                  <ShieldCheck size={28} />
                </div>
                <div className="rsa-special-access-info">
                  <h4 className="rsa-special-access-title">Acesso Administrativo</h4>
                  <p className="rsa-special-access-desc">
                    Você possui acesso total à plataforma Ryan como administrador,
                    incluindo todos os campos, conteúdos e ferramentas de gestão.
                  </p>
                </div>
              </div>
            )}

            {!isAdmin && isVip && (
              <div className="rsa-special-access rsa-special-access--vip">
                <div className="rsa-special-access-icon">
                  <Star size={28} />
                </div>
                <div className="rsa-special-access-info">
                  <h4 className="rsa-special-access-title">Acesso Total VIP</h4>
                  <p className="rsa-special-access-desc">
                    Você possui acesso total a todos os produtos e campos de estudo
                    da plataforma Ryan como membro VIP.
                  </p>
                </div>
              </div>
            )}

            {!isAdmin && !isVip && (
              <>
                {loadingSales ? (
                  <div className="rsa-empty-state">
                    <div className="rsa-spinner rsa-spinner--sm" />
                    <p>Carregando acessos...</p>
                  </div>
                ) : sales.length === 0 ? (
                  <div className="rsa-empty-state">
                    <BookOpen size={40} color="#0D3B0E" style={{ opacity: 0.35 }} />
                    <p>Nenhum acesso encontrado.</p>
                  </div>
                ) : (
                  <div className="rsa-sales-list">
                    {sales.map(sale => {
                      const statusInfo = getSaleStatus(sale);
                      return (
                        <div key={sale._id} className="rsa-sale-item">
                          <div className="rsa-sale-top">
                            <div className="rsa-sale-name-col">
                              <h4 className="rsa-sale-name">
                                {sale.productId?.title || 'Produto não encontrado'}
                              </h4>
                              {sale.productId?.periodo && (
                                <span className="rsa-sale-periodo">
                                  {sale.productId.periodo}° Período
                                </span>
                              )}
                            </div>
                            <span className={`rsa-status-badge rsa-status-badge--${statusInfo.variant}`}>
                              {statusInfo.variant === 'lifetime' ? 'Vitalício' :
                               statusInfo.variant === 'expired' ? 'Expirado' :
                               statusInfo.variant === 'expiring' ? 'Expirando' : 'Ativo'}
                            </span>
                          </div>

                          {statusInfo.variant !== 'lifetime' && (
                            <div className="rsa-validity-bar-wrap">
                              <div
                                className={`rsa-validity-bar rsa-validity-bar--${statusInfo.variant}`}
                                style={{ width: `${statusInfo.pct}%` }}
                              />
                            </div>
                          )}

                          <div className="rsa-sale-meta">
                            <span>
                              <span className="rsa-meta-label">Concedido: </span>
                              {fmtDate(sale.grantedAt)}
                            </span>
                            <span>
                              <span className="rsa-meta-label">Validade: </span>
                              {sale.expiresAt ? fmtDate(sale.expiresAt) : 'Vitalício'}
                            </span>
                            {statusInfo.variant !== 'lifetime' && statusInfo.variant !== 'expired' && (
                              <span className={`rsa-days-left rsa-days-left--${statusInfo.variant}`}>
                                {statusInfo.label}
                              </span>
                            )}
                          </div>

                          {sale.productId?.spaceIds && sale.productId.spaceIds.length > 0 && (
                            <div className="rsa-spaces-row">
                              {sale.productId.spaceIds.map(sp => (
                                <span key={sp._id} className="rsa-space-chip">
                                  <BookOpen size={10} />
                                  {sp.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── ABA SEGURANÇA ── */}
        {activeTab === 'security' && (
          <>
            <div className="rsa-green-card">
              <div className="rsa-card-title-row">
                <h3 className="rsa-card-title">Informações da Conta</h3>
              </div>
              <div className="rsa-info-grid">
                <div className="rsa-info-item">
                  <span className="rsa-info-label">E-mail</span>
                  <span className="rsa-info-value">{user?.email || '—'}</span>
                </div>
                <div className="rsa-info-item">
                  <span className="rsa-info-label">Método de login</span>
                  <span className="rsa-info-value">
                    {isGoogleAuth ? 'Google' : 'E-mail e senha'}
                  </span>
                </div>
              </div>
            </div>

            <div className="rsa-green-card">
              <div className="rsa-card-title-row">
                <h3 className="rsa-card-title">Senha</h3>
                {!isGoogleAuth && !changingPassword && (
                  <button className="rsa-btn-edit" onClick={() => setChangingPassword(true)}>
                    Alterar senha
                  </button>
                )}
              </div>

              {isGoogleAuth ? (
                <p className="rsa-card-desc">
                  Sua conta está vinculada ao Google. Para alterar a senha, acesse as configurações da sua conta Google.
                </p>
              ) : changingPassword ? (
                <div className="rsa-form">
                  {pwError && <div className="rsa-pw-error">{pwError}</div>}
                  {pwSuccess && <div className="rsa-pw-success">Senha alterada com sucesso!</div>}
                  {!pwSuccess && (
                    <>
                      <div className="rsa-form-group">
                        <label className="rsa-form-label">Senha atual</label>
                        <input
                          type="password"
                          className="rsa-form-input"
                          placeholder="••••••••"
                          value={pwForm.current}
                          onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                        />
                      </div>
                      <div className="rsa-form-group">
                        <label className="rsa-form-label">Nova senha</label>
                        <input
                          type="password"
                          className="rsa-form-input"
                          placeholder="Mínimo 6 caracteres"
                          value={pwForm.next}
                          onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}
                        />
                      </div>
                      <div className="rsa-form-group">
                        <label className="rsa-form-label">Confirmar nova senha</label>
                        <input
                          type="password"
                          className="rsa-form-input"
                          placeholder="Repetir nova senha"
                          value={pwForm.confirm}
                          onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                        />
                      </div>
                      <div className="rsa-form-actions">
                        <button
                          className="rsa-btn-cancel"
                          onClick={() => {
                            setChangingPassword(false);
                            setPwForm({ current: '', next: '', confirm: '' });
                            setPwError(null);
                          }}
                        >
                          Cancelar
                        </button>
                        <button className="rsa-btn-save" onClick={handleChangePassword} disabled={pwSaving}>
                          {pwSaving ? 'Salvando...' : 'Salvar'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RyanStudentAreaPage;
